<script setup lang="ts">
/**
 * A password entry with a show/hide toggle: an `AuthField` well with the lock
 * painted at the left and a binocular button at the right. One drawing, used by
 * the join, reset and account-password screens, so the toggle cannot drift
 * between the places a player types a password.
 *
 * The toggle's icon is the painted binoculars (the kit has no eye): dim while
 * the password is hidden, ringed in gold while it shows.
 */
import { ref } from 'vue';

import { useI18n } from '@/i18n';
import AuthField from './auth/AuthField.vue';
import GameIcon from './kit/GameIcon.vue';

withDefaults(
  defineProps<{
    id: string;
    label: string;
    autocomplete?: string;
    placeholder?: string;
    disabled?: boolean;
    dataTest?: string;
    /** The sign-in card names the field by its placeholder; the account sheet shows the label. */
    labelHidden?: boolean;
  }>(),
  { autocomplete: 'off', placeholder: '', disabled: false, dataTest: undefined, labelHidden: false },
);

const model = defineModel<string>({ required: true });
const { t } = useI18n();
const visible = ref(false);
</script>

<template>
  <AuthField
    :id="id"
    v-model="model"
    :label="label"
    :label-hidden="labelHidden"
    :type="visible ? 'text' : 'password'"
    :autocomplete="autocomplete"
    :placeholder="placeholder"
    :disabled="disabled"
    :data-test="dataTest"
  >
    <template #trail>
      <button
        type="button"
        class="toggle"
        :class="{ on: visible }"
        :aria-label="visible ? t('auth.hidePassword') : t('auth.showPassword')"
        :aria-pressed="visible"
        :disabled="disabled"
        @click="visible = !visible"
      >
        <GameIcon name="binoculars" :size="32" />
      </button>
    </template>
  </AuthField>
</template>

<style scoped>
.toggle {
  flex: none;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: none;
  cursor: pointer;
  touch-action: manipulation;
  opacity: 0.55;
  transition: opacity var(--dur) var(--ease);
}

.toggle.on {
  opacity: 1;
  box-shadow: inset 0 0 0 2px var(--kit-gold);
}

.toggle:focus-visible {
  outline: 2px solid var(--kit-gold);
  outline-offset: -2px;
}

.toggle:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}
</style>
